import { Pipe, PipeTransform } from '@angular/core';
import type {Config} from 'dompurify';
import type * as DOMPurify from 'dompurify';

const theDomPurifyGetter: () => typeof DOMPurify = ()=>globalThis.DOMPurify;

export type SanitizeLevels =
    'STRICT' |
    'DEFAULT'|
    'UNSAFE' ;

/**
 * Strictly sanitize a dirty string from any dom tag or attributes
 *
 * @param dirty the string to be sanitized
 */
export function sanitizeDirtyStingAtStrictLevel(dirty: string): string{
    const theConfig: Config & { RETURN_DOM_FRAGMENT?: false | undefined; RETURN_DOM?: false | undefined } = {
        ALLOWED_TAGS: ['#text'],
        KEEP_CONTENT: true
    };
    return theDomPurifyGetter().sanitize(dirty, theConfig);
}

/**
 * Sanitize a dirty string merely allowing a/strong tag and href target attributes
 *
 * @param dirty the string to be sanitized
 */
export function sanitizeDirtyStingAtDefaultLevel(dirty: string): string{
    const theConfig: Config & { RETURN_DOM_FRAGMENT?: false | undefined; RETURN_DOM?: false | undefined } = {
            ALLOWED_TAGS: ['#text', 'a', 'strong', 'br', 'b'],
            ALLOWED_ATTR: ['href', 'target', 'rel'],
            KEEP_CONTENT: true
        };
    return theDomPurifyGetter().sanitize(dirty, theConfig);
}

/**
 * Standalone sanitizeDomString pipe
 *
 * Sanitize a string before putting it into the dom content at different level
 * STRICT level:
 *      no dom tags or attributes would be allowed like:
 *          `<p>HELLO<iframe/\/src=JavScript:alert&lpar;1)></ifrAMe><br>goodbye</p>`
 *      would be sanitized into:
 *          `HELLO                goodbyenot me!`
 * DEFAULT level:
 *      Merely anchor, strong tags and href, target attributes would be allowed like:
 *          `ADLA USql will be deprecated by Feb 29, 2024. Follow <a href="https://foo.com/bar/" target="_blank">link</a> for more info`
 *      would be sanitized into:
 *          `ADLA USql will be deprecated by Feb 29, 2024. Follow <a href="https://foo.com/bar/" target="_blank">link</a> for more info`
 * UNSAFE level:
 *      Nothing would be sanitized, it would directly return the dirty string
 */
@Pipe({
    name: 'sanitizeDomString',
    standalone: true
})
export class SanitizeDomStringPipe implements PipeTransform {

    public transform(dirty: string, level: SanitizeLevels = 'DEFAULT'): string {
        switch (level) {
            case 'UNSAFE':
                return dirty;
            case 'STRICT':
                return sanitizeDirtyStingAtStrictLevel(dirty);
            case 'DEFAULT':
            default:
                return sanitizeDirtyStingAtDefaultLevel(dirty);
        }
    }
}
